import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaEye, FaFilter } from 'react-icons/fa';
import { listOrders, updateOrderStatus } from '../../actions/orderActions';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';
import Paginate from '../../components/ui/Paginate';

const OrdersPage = () => {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [updateOrderId, setUpdateOrderId] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  
  const dispatch = useDispatch();
  
  const orderList = useSelector((state) => state.orderList);
  const { loading, error, orders, pagination } = orderList;
  
  const orderUpdateStatus = useSelector((state) => state.orderUpdateStatus);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = orderUpdateStatus;
  
  useEffect(() => {
    dispatch(listOrders(status, page));
  }, [dispatch, status, page, successUpdate]);
  
  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(1);
    dispatch(listOrders(e.target.value, 1));
  };
  
  const openStatusModal = (orderId, currentStatus) => {
    setUpdateOrderId(orderId);
    setNewStatus(currentStatus);
    setShowStatusModal(true);
  };
  
  const closeStatusModal = () => {
    setUpdateOrderId(null);
    setNewStatus('');
    setShowStatusModal(false);
  };
  
  const updateStatus = () => {
    dispatch(updateOrderStatus(updateOrderId, newStatus));
    setShowStatusModal(false);
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
  };
  
  const getStatusClass = (orderStatus) => {
    switch (orderStatus) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusText = (orderStatus) => {
    switch (orderStatus) {
      case 'pending':
        return '待处理';
      case 'processing':
        return '处理中';
      case 'shipped':
        return '已发货';
      case 'delivered':
        return '已送达';
      case 'cancelled':
        return '已取消';
      default:
        return orderStatus;
    }
  };
  
  return (
    <div className="admin-orders-page">
      <h1 className="text-3xl font-bold mb-6">订单管理</h1>
      
      {loadingUpdate && <Loader />}
      {errorUpdate && <Message variant="error">{errorUpdate}</Message>}
      {successUpdate && <Message variant="success">订单状态已更新</Message>}
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="border-b bg-gray-50 px-6 py-4 flex items-center">
          <FaFilter className="text-gray-400 mr-2" />
          <span className="mr-4">筛选状态:</span>
          <select
            value={status}
            onChange={handleStatusChange}
            className="p-2 border rounded"
          >
            <option value="">所有订单</option>
            <option value="pending">待处理</option>
            <option value="processing">处理中</option>
            <option value="shipped">已发货</option>
            <option value="delivered">已送达</option>
            <option value="cancelled">已取消</option>
          </select>
        </div>
        
        {loading ? (
          <div className="p-6">
            <Loader />
          </div>
        ) : error ? (
          <div className="p-6">
            <Message variant="error">{error}</Message>
          </div>
        ) : orders && orders.length === 0 ? (
          <div className="p-6 text-center">
            <Message>没有找到订单</Message>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      订单号
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      日期
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      客户
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      金额
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      状态
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {order.full_name || '-'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {order.phone_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ¥{parseFloat(order.total_amount).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openStatusModal(order.id, order.status)}
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(
                            order.status
                          )}`}
                        >
                          {getStatusText(order.status)}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/order/${order.id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <FaEye />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="px-6 py-4 border-t">
              {pagination && pagination.totalPages > 1 && (
                <Paginate
                  pages={pagination.totalPages}
                  page={pagination.page}
                  path="/admin/orders"
                  extraParams={status ? `status=${status}&` : ''}
                />
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-xl font-bold mb-4">更新订单状态</h3>
            <p className="mb-4">订单 #{updateOrderId}</p>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">状态</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="pending">待处理</option>
                <option value="processing">处理中</option>
                <option value="shipped">已发货</option>
                <option value="delivered">已送达</option>
                <option value="cancelled">已取消</option>
              </select>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeStatusModal}
                className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
              >
                取消
              </button>
              <button
                onClick={updateStatus}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {loadingUpdate ? <Loader size="sm" /> : '更新状态'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;